
import { SanityDocument as SanityClientDocument } from '@sanity/client';
import { Component, mixins, Prop, Watch } from 'nuxt-property-decorator';
import { ISanityObjects, SanityObjectSchema } from '~/types/sanity-objects';
import SanityPromoItemNav from '~/mixins/SanityPromoItemNav';
import { TRefDoc } from '../Sanity/PromoCarouselSlide.vue';
import { Getter, Action } from 'vuex-class';
import { IProjectTimeline } from '~/types/project';

interface Statuses {
  [key: string]: string;
}

@Component({
  inheritAttrs: false,
})
export default class FilmsPromoPosterSlide extends mixins(SanityPromoItemNav) {
  @Prop(Object)
  readonly item!: ISanityObjects[SanityObjectSchema.PROMO_CAROUSEL_ITEM];
  @Prop(Number)
  readonly slideAspectRatio?: number;
  @Prop(Number)
  readonly slideWidth?: number;
  @Prop({ type: Boolean, default: false })
  readonly portraitMode!: boolean;

  @Action('loadProjectTimeline', { namespace: 'projects' })
  private loadProjectTimeline!: (projectSlug: string) => void;

  @Getter('getProjectTimeline', { namespace: 'projects' })
  readonly getProjectTimeline!: (
    projectSlug: string,
  ) => IProjectTimeline | undefined;

  @Watch('item.documentReference._ref', { immediate: true })
  handleDocRefChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      this.$sanityLookup.fetchReference({ _id: current });
    }
  }

  @Watch('slug', { immediate: true })
  handleSlugChange(current: string | undefined, previous: string | undefined) {
    if (current && current !== previous) {
      this.loadProjectTimeline(current);
    }
  }

  @Watch('firstBadgeRef', { immediate: true })
  handleFirstBadgeRefChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      this.$sanityLookup.fetchReference({ _id: current });
    }
  }

  get projectTimeline() {
    if (!this.slug) return;

    return this.getProjectTimeline(this.slug);
  }

  get projectTimelineStatus() {
    return this.projectTimeline?.steps?.[this.projectTimeline?.currentStepIndex]
      ?.title;
  }

  get firstBadgeRef() {
    return this.item?.badges?.map((x) => x._ref)?.at(0);
  }

  get firstBadgeDoc() {
    const docRefId = this.firstBadgeRef;
    return docRefId
      ? this.$sanityLookup.getDocumentFromReference<
          SanityClientDocument<{ title?: string }>
        >({ _ref: docRefId })
      : {
          isLoading: false,
          hasError: false,
        };
  }

  get firstBadgeTitle() {
    return this.firstBadgeDoc?.data?.title;
  }

  get documentRef() {
    const docRefId = this.item?.documentReference?._ref;
    return docRefId
      ? this.$sanityLookup.getDocumentFromReference<
          SanityClientDocument<TRefDoc & { title?: string }>
        >({ _ref: docRefId })
      : {
          isLoading: false,
          hasError: false,
        };
  }

  get defaultAspectRatio() {
    return 16 / 9;
  }

  get defaultWidth() {
    return 1920;
  }

  get aspectRatio() {
    return this.slideAspectRatio !== undefined
      ? this.slideAspectRatio
      : this.defaultAspectRatio;
  }

  get ariaLabel() {
    const title = this.documentRef?.data?.title;
    return title
      ? this.$t('components.films.moreFilmsSlide.ctaWithTitle', { title })
      : this.$t('components.films.moreFilmsSlide.cta');
  }

  get logo() {
    return this.documentRef?.data?.promoCard?.logo;
  }

  get slug() {
    return this.documentRef?.data?.slug;
  }

  get height() {
    return Math.round(this.width * (1 / this.aspectRatio));
  }

  get width() {
    return this.slideWidth !== undefined ? this.slideWidth : this.defaultWidth;
  }

  get isLoading() {
    return this.documentRef?.isLoading;
  }

  get imageUrl() {
    const img = this.portraitMode
      ? this.documentRef?.data?.promoCard?.heroImagePortrait
      : this.documentRef?.data?.promoCard?.heroImageLandscape;
    if (!img) {
      return;
    }

    return this.$sanityImage
      .urlFor(img)
      .width(this.width)
      .height(this.height)
      .fit('crop')
      .url();
  }

  get overline() {
    return this.documentRef?.data?.genres || [];
  }
}
